import {
  BoidAssignment,
  BoidAssignmentInput,
  SubAccount
} from "@common-core/coat-operational-hierarchy-appsync-model";

/**
 * The {@link BoidAssignmentState} type is used to hold the
 * assignment state of a BOID for editing purposes.
 *
 * The {@link statusFor} function can be used to determine a status
 * for the given state relative to the operational entity being edited.
 *
 * @see {@link statusFor}
 */
export interface BoidAssignmentState {
  type: string;
  id: string;

  /**
   * The id of the CACM client entity that "owns" the business operation relationship.
   *
   * When undefined, the boid can be considered an orphan. This can
   * happen when a source client entity is unlinked from the derived operational
   * entity, yet the BOID assignment is still present.
   */
  sourceCaId?: string | null;

  /**
   * The id of the operational entity to which the business operation is assigned.
   */
  assignedTo?: string | null;

  subAccountStatus?: string | null; // Optional, used for sub-account status
}

/**
 * The {@link BoidAssignmentStatus} type is used to represent the 4 states in which
 * a BOID assignment can exist relative to the {@link OperationalEntity} being
 * edited.
 *
 * <dl>
 *   <dt>assigned</dt>
 *   <dd>The BOID is assigned to the current operational entity</dd>
 *   <dt>available</dt>
 *   <dd>The BOID is available for assignment to the current operational entity</dd>
 *   <dt>unavailable</dt>
 *   <dd>The BOID has been assigned to another operational entity</dd>
 *   <dt>orphaned</dt>
 *   <dd>The BOID is assigned to the current operational entity;
 *   however the CACM entity that owns the BOID has been
 *   unlinked from the operational entity</dd>
 * </dl>
 */
export type BoidAssignmentStatus =
  | "assigned"
  | "available"
  | "unavailable"
  | "orphaned";

/**
 * The {@link statusFor} function computes the assignment status of the
 * specified {@link BoidAssignmentState} instance relative to the
 * given {@link OperationalEntity}
 *
 * @param state
 *
 * @param entityId
 * @see {@link BoidAssignmentStatus}
 * @see {@link BoidAssignmentState}
 * @see {@link OperationalEntity}
 */
export const statusFor = (
  state: BoidAssignmentState,
  entityId: string
): BoidAssignmentStatus => {
  // If the state does not indicate an owner, it is considered orphaned
  if (!state.sourceCaId) {
    return "orphaned";
  }
  // If the state indicates the business operation is not assigned to an entity,
  // it is considered available.
  if (!state.assignedTo) {
    return "available";
  }
  // If the state indicates the business operation is assigned to the entity, it is considered assigned;
  // otherwise it has been assigned to another entity and is unavailable
  return state.assignedTo === entityId ? "assigned" : "unavailable";
};

/**
 * The {@link createBoidAssignment} function creates a {@link BoidAssignment}
 * from the a {@link BoidAssignmentState} for the specified entity id.
 *
 * @param state
 * @param entityId
 */
export const createBoidAssignment = (
  state: BoidAssignmentState,
  entityId: string
): BoidAssignmentInput => ({
  commonOrgId: entityId,
  businessOperationId: state.id,
  businessOperationType: state.type
});

/**
 * The {@link fromBoidAssignment} mapping function creates a {@link BoidAssignmentState}
 * instance from a {@link BoidAssignment} instance.
 *
 * @param assignment
 */
export const fromBoidAssignment = (
  assignment: BoidAssignment
): BoidAssignmentState => ({
  type: assignment.businessOperationType,
  id: assignment.businessOperationId,
  assignedTo: assignment.commonOrgId
});

export type AssignmentStateFromSubAccount = (
  subAccount: SubAccount
) => BoidAssignmentState;

/**
 * The {@link fromSubAccount} high-order function returns a mapping function
 * that creates a {@link BoidAssignmentState} instance from a {@link SubAccount}
 * instance.
 *
 * @param sourceCaId
 */
export const fromSubAccount = (
  sourceCaId: string
): AssignmentStateFromSubAccount => {
  return subAccount => ({
    type: subAccount.subAccountType,
    id: subAccount.subAccountId,
    subAccountStatus: subAccount.subAccountStatus,
    sourceCaId,
    assignedTo: subAccount.assignedCommonOrgId
  });
};

/**
 * A sort function that sorts a {@link NamedBoidGroup} list by their name.
 *
 * @param a
 * @param b
 */
export const byBoid = (a: BoidAssignmentState, b: BoidAssignmentState) =>
  a.id.localeCompare(b.id);

/**
 * The {@link boidAssignmentStateKey} function creates a unique key for a
 * {@link BoidAssignmentState} by combining the type and id properties.
 *
 * @param state The {@link BoidAssignmentState} for which to generate a key
 * @returns A string key in the format "{type}-{id}"
 * @see {@link BoidAssignmentState}
 */
export const boidAssignmentStateKey = (state: BoidAssignmentState): string => {
  return `${state.type}-${state.id}`.toLowerCase();
};
